/**
 * Helper functions for handling date/time formatting and timezone conversions
 */
export class DateUtils {

  /**
   * Get the browser's timezone identifier
   * @returns Timezone string (e.g., "Europe/Helsinki", "America/New_York")
   */
  public static getTimezone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  /**
   * Get the browser's timezone offset in minutes
   * @returns Offset in minutes (negative = ahead of UTC)
   */
  public static getTimezoneOffset(): number {
    return new Date().getTimezoneOffset();
  }

  /**
   * Convert a UTC date string to local time and format it
   * @param utcDateString Date string in UTC (e.g., "2024-01-15 14:30:00" or ISO format)
   * @param format Format type: 'date', 'time', 'datetime', 'dateonly', 'timeonly'
   * @returns Formatted date string in local timezone
   */
  public static formatUtcToLocal(utcDateString: string | null | undefined, format: 'date' | 'time' | 'datetime' | 'dateonly' | 'timeonly' = 'datetime'): string {
    if (!utcDateString) return '';

    try {
      let date: Date;

      if (utcDateString.includes('Z') || utcDateString.includes('+') || utcDateString.includes('T')) {
        date = new Date(utcDateString);
      } else {
        date = new Date(utcDateString.replace(' ', 'T') + 'Z');
      }

      if (isNaN(date.getTime())) {
        return utcDateString;
      }

      switch (format) {
        case 'date':
        case 'dateonly':
          return DateUtils.formatDate(date);
        case 'time':
        case 'timeonly':
          return DateUtils.formatTime(date);
        case 'datetime':
        default:
          return `${DateUtils.formatDate(date)} ${DateUtils.formatTime(date)}`;
      }
    } catch {
      return utcDateString;
    }
  }

  /**
   * Format a Date object to dd.MM.yyyy format
   * @param date Date object
   * @returns Formatted date string
   */
  public static formatDate(date: Date): string {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  }

  /**
   * Format a Date object to HH:mm format
   * @param date Date object
   * @returns Formatted time string
   */
  public static formatTime(date: Date): string {
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  /**
   * Parse a datetime string and return hours in HH:mm format (local time)
   * @param dateTimeString DateTime string (UTC)
   * @returns Time in HH:mm format
   */
  public static toLocalHours(dateTimeString: string | null | undefined): string {
    return DateUtils.formatUtcToLocal(dateTimeString, 'time');
  }

  /**
   * Parse a datetime string and return date in dd.MM.yyyy format (local time)
   * @param dateTimeString DateTime string (UTC)
   * @returns Date in dd.MM.yyyy format
   */
  public static toLocalDate(dateTimeString: string | null | undefined): string {
    return DateUtils.formatUtcToLocal(dateTimeString, 'date');
  }
}
